import { useEffect, useMemo, useState } from "react";
import CalcMaintenanceBox from "../../common/CalcMaintenanceBox";
import CalcVRBox from "../../common/CalcVRBox";
import Control from "../../common/Control";
import DynamicList from "../../common/DynamicList";
import InputGroupWithResetValue from "../../common/InputGroupWithResetValue";
import Loader from "../../common/Loader";
import {
  PRESTATION_ASSISTANCE,
  PRESTATION_REMISE_EN_ETAT,
} from "./hooks/useQuotationDetail";
import DetailSectionCard from "./DetailSectionCard";
import ToggleDetailButton from "./ToggleDetailButton";
import PrestationHitechPreviewModal from "./PrestationHitechPreviewModal";
import { prestationService } from "../../../services/prestationService";
import { modalActions } from "../../../context/actions/modalActions";
import useStore from "../../../context/useStore";
import tools from "../../../helpers/tools";
import { formatPrestationLabel } from "../../../helpers/prestationTools";
import styles from "./QuotationDetailTable.module.scss";

const QuotationDetailTable = ({
  quotation,
  dossierUuid,
  rlChange,
  resetDefault,
  index,
  doesToggleProtectedFields,
  funding,
  tireData,
  constantData,
  state,
  financeRentCalc,
  applicationFeeCalc,
  mentionSel,
  setMentionSel,
  addMention,
  onToggleDetails,

  showDetail,
}) => {
  const [storeState, dispatch] = useStore();
  const modal_actions = modalActions(storeState, dispatch);
  const authData = storeState.auth;

  const [prestationsCatalog, setPrestationsCatalog] = useState([]);
  const catalog = quotation.vehicle?.catalog
    ? tools.getHitechCatalogFromGeographicZone(quotation.vehicle.catalog)
    : null;

  useEffect(() => {
    if (!catalog) {
      return;
    }
    prestationService
      .getPrestations(catalog)
      .then((res) => setPrestationsCatalog(res.data))
      .catch(() => setPrestationsCatalog([]));
  }, [catalog]);

  // Copie d'affichage : le libellé brut du catalogue reste la valeur enregistrée dans
  // `benefit.name`, seul le texte proposé dans le sélecteur est mis en forme.
  const prestationsCatalogOptions = useMemo(
    () =>
      prestationsCatalog.map((p) => ({
        ...p,
        libelleAffichage: formatPrestationLabel(p.libelle),
      })),
    [prestationsCatalog]
  );

  const [selectedPrestationMapping, setSelectedPrestationMapping] =
    useState("");

  function addPrestationFromCatalog(mapping) {
    const prestation = prestationsCatalog.find((p) => p.mapping === mapping);
    if (!prestation) {
      return;
    }
    rlChange({
      target: {
        name: "benefits",
        value: [
          ...quotation.benefits,
          { name: prestation.libelle, priceHT: 0, mapping: prestation.mapping },
        ],
      },
    });
    setSelectedPrestationMapping("");
  }

  function openPrestationHitechPreview() {
    modal_actions.updateModal({
      isOpen: true,
      content: (
        <PrestationHitechPreviewModal
          quotationUuid={dossierUuid}
          quotationDetailUuid={quotation.uuid}
        />
      ),
    });
  }

  return (
    <div className={styles.detailZone}>
      <div className={styles.detailGrid}>
        <DetailSectionCard
          title="Financement"
          icon="euro-sign"
          accent="red"
          data-priv="invisible-commerciaux"
        >
          {constantData.isLoading ? (
            <Loader />
          ) : (
            funding.map((x, k) => (
              <InputGroupWithResetValue
                key={k}
                props={x}
                data={quotation}
                resetDefault={resetDefault}
                inputChange={rlChange}
                error={state.error[x.name]}
              />
            ))
          )}
          <InputGroupWithResetValue
            props={{
              name: "applicationFee",
              defaultValue: applicationFeeCalc,
              label: "Frais de dossier",
              symbol: "€",
            }}
            data={quotation}
            resetDefault={(name, value) =>
              rlChange({
                target: {
                  name,
                  value,
                },
              })
            }
            inputChange={rlChange}
          />
          {constantData.isLoading ? (
            <Loader />
          ) : (
            <Control
              label="Loyer financier (calculé)"
              disabled={true}
              name="financeRent"
              value={financeRentCalc}
              suffix="€"
            />
          )}
        </DetailSectionCard>

        <DetailSectionCard title="Valeur de rachat" icon="undo" accent="blue">
          <CalcVRBox
            quotation={quotation}
            rlChange={rlChange}
            disabled={doesToggleProtectedFields}
          />
        </DetailSectionCard>

        <DetailSectionCard
          title="Coût de maintenance"
          icon="wrench"
          accent="orange"
        >
          <CalcMaintenanceBox
            quotation={quotation}
            rlChange={rlChange}
            num={index + 1}
            disabled={doesToggleProtectedFields}
          />
        </DetailSectionCard>

        <DetailSectionCard title="Prestations" icon="plus" accent="green">
          <div className={styles.twoCol}>
            <Control
              label="Nombre de pneus"
              name="nbTires"
              type="number"
              change={rlChange}
              value={quotation.nbTires}
            />
            <Control
              label={
                "Prix d'un pneu (par défaut : " +
                (tireData.find((t) => t.uuid == quotation.vehicle.tire?.uuid)
                  ? tireData.find(
                      (t) => t.uuid == quotation.vehicle.tire?.uuid
                    ).price
                  : 0) +
                "€)"
              }
              name="customTirePrice"
              type="number"
              change={rlChange}
              value={quotation.customTirePrice}
              disabled={doesToggleProtectedFields}
            />
          </div>
          <Control
            label={`Catégorie du véhicule (${quotation.vehicleCategory})`}
            name="vehicleCategory"
            type="select"
            datas={constantData.CONTRAT.VEHICLE_CATEGORY}
            dataIndex="id"
            dataLabel="name"
            value={quotation.vehicleCategory}
            change={rlChange}
          />
          <Control
            label="Franchise dommages, vol, incendie, bis de glace"
            value={quotation.franchiseDommage}
            change={rlChange}
            name="franchiseDommage"
            suffix={"€"}
            disabled={doesToggleProtectedFields}
          />
          <Control
            label="Ajouter une prestation Assistance"
            checked={quotation.withOptionAssistance}
            change={rlChange}
            name="withOptionAssistance"
            type="checkbox"
          />
          <Control
            label="Ajouter une prestation Remise en état"
            checked={quotation.withOptionRemiseEnEtat}
            change={rlChange}
            name="withOptionRemiseEnEtat"
            type="checkbox"
          />
          {authData?.user?.email?.endsWith("@10positif.com") && (
            <button
              type="button"
              className="btn btn-default btn-sm mb-2"
              onClick={openPrestationHitechPreview}
            >
              <i className="fa fa-eye mr-2" />
              Vérifier prestations HiTech
            </button>
          )}
          {prestationsCatalog.length > 0 && (
            <Control
              label="Ajouter une prestation depuis le catalogue HiTech"
              name="prestationCatalogPick"
              type="select"
              datas={prestationsCatalogOptions}
              dataIndex="mapping"
              dataLabel="libelleAffichage"
              value={selectedPrestationMapping}
              change={(e) => addPrestationFromCatalog(e.target.value)}
            />
          )}
          <DynamicList
            uniqueKey={quotation.uuid}
            items={quotation.benefits.map((b) => {
              if (
                b.name === PRESTATION_ASSISTANCE.key ||
                b.name === PRESTATION_REMISE_EN_ETAT.key
              ) {
                return { ...b, isLinked: true };
              }
              return b;
            })}
            element="benefits"
            fields={[
              { name: "name", type: "text", label: "Nature" },
              {
                name: "priceHT",
                type: "number",
                label: "Prix HT",
                suffix: "€",
              },
            ]}
            change={rlChange}
            btnLabel="Ajouter une prestation en texte libre"
          />
        </DetailSectionCard>
      </div>

      <div className={styles.fullGrid}>
        <DetailSectionCard title="Mentions" icon="paragraph" accent="gray">
          <DynamicList
            uniqueKey={quotation.uuid}
            items={quotation.mentions}
            element="mentions"
            change={rlChange}
            fields={[{ name: "name", type: "text", label: "" }]}
          />
          <div className="d-flex align-items-center mt-2">
            <Control
              type="select"
              style={{ maxWidth: 260 }}
              datas={constantData.QUOTATION_MENTIONS}
              name="mentionSel"
              dataIndex="name"
              dataLabel="name"
              value={mentionSel}
              change={(e) => setMentionSel(e.target.value)}
            />
            <button className="btn btn-outline-info ml-2" onClick={addMention}>
              <i className="fa fa-plus mr-2"></i>Ajouter une mention
            </button>
          </div>
        </DetailSectionCard>

        <DetailSectionCard title="Commentaire" icon="pen" accent="gray">
          <Control
            value={quotation.comment}
            change={rlChange}
            name="comment"
            type="textarea"
          />
        </DetailSectionCard>
      </div>

      <ToggleDetailButton
        onToggleDetails={onToggleDetails}
        showDetail={showDetail}
      />
    </div>
  );
};

export default QuotationDetailTable;
